import React, { useMemo } from "react";
import { Box, Typography, Button, Table, Sheet, Alert } from "@mui/joy";
import { Check, X } from "lucide-react";

import { PriceComparisonSkeleton } from "./Skeleton";
import { IconButton } from "../../../shared";

/** Fits `$999,999.99` at tabular nums. */
const PRICE_BLOCK_WIDTH = 108;
const CHECK_SLOT_WIDTH = 18;
const SUPPLIER_COLUMN_MIN_WIDTH = PRICE_BLOCK_WIDTH + CHECK_SLOT_WIDTH + 16;

function EfficientDot() {
    return (
        <Box
            component="span"
            sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: "success.500",
                flexShrink: 0,
                display: "inline-block",
            }}
        />
    );
}

const actionButtonSx = {
    borderRadius: "4px",
    fontWeight: 600,
} as const;

function formatMoney(value: number) {
    return `$${value.toFixed(2)}`;
}

function SelectedIcon() {
    return (
        <Box
            sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: "primary.500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <Check
                size={10}
                strokeWidth={3}
                color="var(--joy-palette-primary-solidColor, #fff)"
            />
        </Box>
    );
}

function UnselectedIcon() {
    return (
        <Box
            sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "1.5px solid",
                borderColor: "neutral.outlinedBorder",
                bgcolor: "background.level1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                opacity: 0.55,
            }}
        >
            <Check
                size={9}
                strokeWidth={2.5}
                color="var(--joy-palette-neutral-400)"
            />
        </Box>
    );
}

function PartialSelectedIcon() {
    return (
        <Box
            sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: "primary.500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 2,
                    borderRadius: 1,
                    bgcolor: "var(--joy-palette-primary-solidColor, #fff)",
                }}
            />
        </Box>
    );
}

type ColumnSelectionState = "none" | "partial" | "full";

function SelectionIndicator({
    state,
    showUnselected = true,
}: {
    state: ColumnSelectionState;
    showUnselected?: boolean;
}) {
    if (state === "full") return <SelectedIcon />;
    if (state === "partial") return <PartialSelectedIcon />;
    if (showUnselected) return <UnselectedIcon />;
    return null;
}

function FooterTotalCell({
    total,
    selectedTotal,
    isBest,
    selectionState,
}: {
    total: number;
    selectedTotal: number;
    isBest: boolean;
    selectionState: ColumnSelectionState;
}) {
    const isPartial = selectionState === "partial";

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.75,
                width: "100%",
                py: 0.25,
            }}
        >
            <Box
                sx={{
                    width: CHECK_SLOT_WIDTH,
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                }}
            >
                <SelectionIndicator
                    state={selectionState}
                    showUnselected={total > 0}
                />
            </Box>

            <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                    }}
                >
                    <Typography
                        level="title-md"
                        sx={{
                            fontWeight: 700,
                            fontSize: "1rem",
                            fontVariantNumeric: "tabular-nums",
                            color: isPartial
                                ? "primary.500"
                                : isBest
                                  ? "success.500"
                                  : "text.primary",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {formatMoney(isPartial ? selectedTotal : total)}
                    </Typography>
                    {!isPartial && isBest && <EfficientDot />}
                </Box>
                {isPartial && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: -0.15,
                        }}
                    >
                        <Typography
                            level="body-xs"
                            sx={{
                                fontSize: "0.6875rem",
                                fontWeight: 500,
                                fontVariantNumeric: "tabular-nums",
                                color: "text.tertiary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {formatMoney(total)} total
                        </Typography>
                        {isBest && <EfficientDot />}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function getSupplierColumnSelectionState(
    supplierName: string,
    productRows: Array<{
        ProductName: string;
        ManufacturerProductId?: string;
        prices: Record<string, number>;
    }>,
    itemSupplierSelections: Record<string, string>,
): ColumnSelectionState {
    const applicableRows = productRows.filter(
        (row) => (row.prices[supplierName] || 0) > 0,
    );

    if (applicableRows.length === 0) {
        return "none";
    }

    const selectedCount = applicableRows.filter(
        (row) => itemSupplierSelections[rowSelectionKey(row)] === supplierName,
    ).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === applicableRows.length) return "full";
    return "partial";
}

function getSupplierColumnSelectedTotal(
    supplierName: string,
    productRows: Array<{
        ProductName: string;
        ManufacturerProductId?: string;
        Quantity: number;
        prices: Record<string, number>;
    }>,
    itemSupplierSelections: Record<string, string>,
) {
    return productRows.reduce((sum, row) => {
        const price = row.prices[supplierName] || 0;
        if (price <= 0) return sum;
        if (itemSupplierSelections[rowSelectionKey(row)] !== supplierName) {
            return sum;
        }
        return sum + price * row.Quantity;
    }, 0);
}

function PriceCellContent({
    price,
    quantity,
    isEfficient,
    isSelected,
}: {
    price: number;
    quantity: number;
    isEfficient: boolean;
    isSelected: boolean;
}) {
    if (price === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 0.75,
                    width: "100%",
                }}
            >
                <Box sx={{ width: CHECK_SLOT_WIDTH, flexShrink: 0 }} />
                <Typography
                    sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "text.tertiary",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    —
                </Typography>
            </Box>
        );
    }

    const totalPrice = price * quantity;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.75,
                width: "100%",
            }}
        >
            <Box
                sx={{
                    width: CHECK_SLOT_WIDTH,
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                }}
            >
                {isSelected ? <SelectedIcon /> : <UnselectedIcon />}
            </Box>

            <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Typography
                    sx={{
                        fontSize: "0.875rem",
                        fontWeight: isSelected || isEfficient ? 700 : 500,
                        fontVariantNumeric: "tabular-nums",
                        color: isSelected
                            ? "primary.500"
                            : isEfficient
                              ? "success.500"
                              : "text.primary",
                        whiteSpace: "nowrap",
                    }}
                >
                    {formatMoney(totalPrice)}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: -0.15,
                    }}
                >
                    <Typography
                        level="body-xs"
                        sx={{
                            fontSize: "0.6875rem",
                            fontWeight: isEfficient ? 600 : 500,
                            fontVariantNumeric: "tabular-nums",
                            color: isEfficient
                                ? "success.500"
                                : "text.tertiary",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {formatMoney(price)}/u
                    </Typography>
                    {isEfficient && <EfficientDot />}
                </Box>
            </Box>
        </Box>
    );
}

interface InventoryItem {
    ProductName: string;
    ProductCategory?: string;
    ManufacturerProductId?: string;
    LaborServiceId?: string;
    UnitPrice: number | string;
    Quantity: number | string;
}

function rowSelectionKey(row: {
    ProductName: string;
    ManufacturerProductId?: string;
    LaborServiceId?: string;
}): string {
    return (
        row.ManufacturerProductId || row.LaborServiceId || row.ProductName
    );
}

interface Supplier {
    SupplierName: string;
    SupplierInventory: InventoryItem[];
}

type PurchaseStrategy = "single" | "multiple";

interface SupplierComparisonProps {
    comparisonResults: Supplier[];
    isLoading?: boolean;
    purchaseStrategy?: PurchaseStrategy;
    onClose?: () => void;
    onConfirm?: () => void;
    itemSupplierSelections?: Record<string, string>;
    onItemSupplierSelect?: (productName: string, supplierName: string) => void;
    selectedSingleSupplier?: { id?: string; name?: string } | null;
    onSingleSupplierSelect?: (supplier: { id: string; name: string }) => void;
}

const SupplierComparison: React.FC<SupplierComparisonProps> = ({
    comparisonResults,
    isLoading = false,
    purchaseStrategy = "single",
    onClose,
    onConfirm,
    itemSupplierSelections = {},
    onItemSupplierSelect,
    selectedSingleSupplier,
    onSingleSupplierSelect,
}) => {
    const { productRows, totals, efficiencyMap } = useMemo(() => {
        if (isLoading || !comparisonResults || comparisonResults.length === 0) {
            return { productRows: [], totals: {}, efficiencyMap: {} };
        }

        const productMap: Record<string, any> = {};
        const supplierTotals: Record<string, number> = {};
        const bestPricePerProductMap: Record<
            string,
            { supplier: string; price: number }
        > = {};

        comparisonResults.forEach((supplier) => {
            supplierTotals[supplier.SupplierName] = 0;
            supplier.SupplierInventory.forEach((product) => {
                if (!product.ProductName) return;
                const key =
                    product.ManufacturerProductId ||
                    product.LaborServiceId ||
                    product.ProductName;

                if (!productMap[key]) {
                    productMap[key] = {
                        ProductName: product.ProductName,
                        ManufacturerProductId: product.ManufacturerProductId,
                        LaborServiceId: product.LaborServiceId,
                        ProductCategory: product.ProductCategory,
                        Quantity: Number(product.Quantity),
                        prices: {},
                    };
                }

                const price = Number(product.UnitPrice) || 0;
                productMap[key].prices[supplier.SupplierName] = price;

                if (price > 0) {
                    const totalPrice = price * Number(product.Quantity);
                    supplierTotals[supplier.SupplierName] += totalPrice;

                    if (
                        !bestPricePerProductMap[key] ||
                        price < bestPricePerProductMap[key].price
                    ) {
                        bestPricePerProductMap[key] = {
                            supplier: supplier.SupplierName,
                            price,
                        };
                    }
                }
            });
        });

        const efficiencyMap: Record<string, string> = {};
        Object.keys(bestPricePerProductMap).forEach((key) => {
            efficiencyMap[key] = bestPricePerProductMap[key].supplier;
        });

        return {
            productRows: Object.values(productMap),
            totals: supplierTotals,
            efficiencyMap,
        };
    }, [comparisonResults]);

    const bestTotalSupplier = useMemo(() => {
        if (Object.keys(totals).length === 0) return null;
        return Object.entries(totals).reduce(
            (best, [name, total]) =>
                !best || total < best.total ? { name, total } : best,
            null as any,
        );
    }, [totals]);

    const isSelectionComplete = useMemo(() => {
        if (purchaseStrategy === "single") {
            return !!selectedSingleSupplier?.id;
        } else {
            return productRows.every(
                (row) => !!itemSupplierSelections[rowSelectionKey(row)],
            );
        }
    }, [
        purchaseStrategy,
        selectedSingleSupplier,
        itemSupplierSelections,
        productRows,
    ]);

    function getFooterColumnSelectionState(
        supplierName: string,
    ): ColumnSelectionState {
        if (purchaseStrategy === "single") {
            return selectedSingleSupplier?.id === supplierName
                ? "full"
                : "none";
        }

        return getSupplierColumnSelectionState(
            supplierName,
            productRows,
            itemSupplierSelections,
        );
    }

    if (comparisonResults.length === 0)
        return (
            <Box p={2}>
                <Alert color="neutral">No comparison data.</Alert>
            </Box>
        );

    if (isLoading) {
        return (
            <PriceComparisonSkeleton
                supplierCount={comparisonResults.length || 3}
            />
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                bgcolor: "background.surface",
            }}
        >
            <Box
                sx={{
                    pl: 2,
                    pr: 2.5,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Box>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                        {purchaseStrategy === "single"
                            ? "Single Source Strategy"
                            : "Multi-Source Strategy"}
                    </Typography>
                    <Typography
                        level="body-xs"
                        color={isSelectionComplete ? "success" : "warning"}
                    >
                        {isSelectionComplete
                            ? "Selection complete. Ready to apply."
                            : purchaseStrategy === "single"
                              ? "Please select a global supplier."
                              : `Missing selections for ${productRows.filter((r) => !itemSupplierSelections[rowSelectionKey(r)]).length} row(s)`}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="solid"
                        color="primary"
                        size="sm"
                        disabled={!isSelectionComplete}
                        onClick={onConfirm}
                        sx={{ ...actionButtonSx, px: 2.5, fontWeight: 700 }}
                    >
                        Apply strategy
                    </Button>
                    <IconButton
                        variant="soft"
                        color="primary"
                        size="sm"
                        onClick={onClose}
                        sx={actionButtonSx}
                    >
                        <X size={20} />
                    </IconButton>
                </Box>
            </Box>

            <Sheet sx={{ flex: 1, overflow: "auto" }}>
                <Table
                    stickyHeader
                    size="sm"
                    borderAxis="bothBetween"
                    stripe="odd"
                    hoverRow
                    sx={{
                        height: "100%",
                        "& tr > *:first-of-type": {
                            position: "sticky",
                            left: 0,
                            boxShadow: "1px 0 var(--TableCell-borderColor)",
                            bgcolor: "background.surface",
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 220, paddingLeft: 16 }}>
                                <Typography
                                    level="title-sm"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Line Item
                                </Typography>
                            </th>
                            {comparisonResults.map((s) => (
                                <th
                                    key={s.SupplierName}
                                    style={{
                                        textAlign: "left",
                                        minWidth: SUPPLIER_COLUMN_MIN_WIDTH,
                                        paddingLeft: 12,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={0.5}
                                        py={0.5}
                                    >
                                        <Typography
                                            level="title-sm"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {s.SupplierName}
                                        </Typography>
                                    </Box>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {productRows.map((row) => (
                            <tr key={rowSelectionKey(row)}>
                                <td style={{ paddingLeft: "16px" }}>
                                    <Box>
                                        <Typography
                                            level="title-sm"
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {row.ProductName}
                                        </Typography>
                                        <Typography
                                            level="body-xs"
                                            sx={{ fontSize: "0.68rem" }}
                                        >
                                            {row.Quantity} ·{" "}
                                            {row.ProductCategory || "Material"}
                                        </Typography>
                                    </Box>
                                </td>
                                {comparisonResults.map((s) => {
                                    const price =
                                        row.prices[s.SupplierName] || 0;
                                    const rowKey = rowSelectionKey(row);
                                    const isEfficient =
                                        efficiencyMap[rowKey] ===
                                        s.SupplierName;
                                    const isSelected =
                                        (purchaseStrategy === "multiple"
                                            ? itemSupplierSelections[rowKey] ===
                                              s.SupplierName
                                            : selectedSingleSupplier?.id ===
                                              s.SupplierName) && price > 0;
                                    const isClickable =
                                        purchaseStrategy === "multiple" &&
                                        price > 0;

                                    return (
                                        <td
                                            key={s.SupplierName}
                                            onClick={() =>
                                                isClickable &&
                                                onItemSupplierSelect
                                                    ? onItemSupplierSelect(
                                                          rowKey,
                                                          s.SupplierName,
                                                      )
                                                    : null
                                            }
                                            style={{
                                                textAlign: "left",
                                                cursor: isClickable
                                                    ? "pointer"
                                                    : "default",
                                                opacity: price === 0 ? 0.6 : 1,
                                                backgroundColor: isSelected
                                                    ? "var(--joy-palette-primary-softBg)"
                                                    : undefined,
                                                paddingLeft: 12,
                                            }}
                                        >
                                            <PriceCellContent
                                                price={price}
                                                quantity={row.Quantity}
                                                isEfficient={isEfficient}
                                                isSelected={isSelected}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        <tr style={{ height: "100%" }}>
                            <td
                                colSpan={comparisonResults.length + 1}
                                style={{ border: "none", padding: 0 }}
                            />
                        </tr>
                    </tbody>
                    <tfoot
                        style={{
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: "rgba(128, 128, 128, 0.05)",
                            backdropFilter: "blur(12px)",
                            zIndex: 100,
                            boxShadow:
                                "0 -4px 12px rgba(0,0,0,0.05), 0 -1px 0 rgba(128, 128, 128, 0.2)",
                        }}
                    >
                        <tr>
                            <td
                                style={{
                                    fontWeight: 700,
                                    paddingLeft: "16px",
                                    fontSize: "0.8125rem",
                                    color: "var(--joy-palette-neutral-600)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                Estimated price
                            </td>
                            {comparisonResults.map((s) => {
                                const isBest =
                                    bestTotalSupplier?.name === s.SupplierName;
                                const selectionState =
                                    getFooterColumnSelectionState(
                                        s.SupplierName,
                                    );
                                const columnTotal = totals[s.SupplierName] ?? 0;
                                const selectedTotal =
                                    purchaseStrategy === "multiple"
                                        ? getSupplierColumnSelectedTotal(
                                              s.SupplierName,
                                              productRows,
                                              itemSupplierSelections,
                                          )
                                        : columnTotal;

                                return (
                                    <td
                                        key={s.SupplierName}
                                        style={{
                                            textAlign: "left",
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            paddingLeft: 12,
                                            backgroundColor:
                                                selectionState === "full"
                                                    ? "var(--joy-palette-primary-softBg)"
                                                    : selectionState ===
                                                        "partial"
                                                      ? "rgba(var(--joy-palette-primary-mainChannel) / 0.06)"
                                                      : undefined,
                                        }}
                                    >
                                        <FooterTotalCell
                                            total={columnTotal}
                                            selectedTotal={selectedTotal}
                                            isBest={isBest}
                                            selectionState={selectionState}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    </tfoot>
                </Table>
            </Sheet>
        </Box>
    );
};

export { SupplierComparison };
